<script setup lang="ts">
import type {
  Component,
  FunctionalComponent,
  HTMLAttributes,
  VNodeProps
} from "vue"
import LoadingIcon from "@/components/icons/LoadingIcon.vue"

interface Props {
  type?: "submit" | "button" | "reset"
  text: string
  disabled?: boolean
  isLoading?: boolean
  classButton?: string
  icon?: FunctionalComponent<HTMLAttributes & VNodeProps> | Component
  imageIcon?: string
  iconRight?: FunctionalComponent<HTMLAttributes & VNodeProps> | Component
}

withDefaults(defineProps<Props>(), {
  type: "submit",
  disabled: false,
  isLoading: false,
  classButton: "",
  icon: undefined,
  imageIcon: undefined,
  iconRight: undefined
})
</script>

<template>
  <button
    :type="type"
    :disabled="isLoading || disabled"
    class="flex justify-center items-center gap-3 whitespace-nowrap"
    :class="[classButton, text ? 'px-4' : 'px-2']"
  >
    <component
      :is="icon"
      v-if="icon && !isLoading"
      class="w-6 h-6"
      aria-hidden="true"
    />
    <LoadingIcon v-if="isLoading" class="text-white" />
    <img
      v-else-if="imageIcon"
      :src="imageIcon"
      :alt="imageIcon.split('.')[0] || ''"
      class="h-6 max-w-6"
    />
    <span v-if="text">
      {{ text }}
    </span>
    <component
      :is="iconRight"
      v-if="iconRight"
      class="w-6 h-6"
      aria-hidden="true"
    />
  </button>
</template>

<style scoped>
button,
[type="button"],
[type="reset"],
[type="submit"] {
  @apply py-[0.313rem] border-2 border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-secondary active:bg-clicked focus:outline-none focus:ring-0 focus:ring-offset-0 focus:ring-primary transition-all disabled:bg-disabled disabled:text-placeholderText disabled:cursor-not-allowed;
}

button.secondary {
  @apply bg-transparent disabled:bg-transparent border-primary disabled:border-disabled text-textBlack dark:text-white disabled:text-placeholderText dark:disabled:text-placeholderText;
}

button.unstyle {
  @apply bg-gray-200 dark:bg-gray-700 hover:bg-gray-200 hover:dark:bg-gray-600 border-transparent text-textBlack dark:text-white;
}

button.small {
  @apply py-1;
}
</style>
